"use client";

import { useState, useEffect } from "react";
import {
  X,
  Phone,
  Lock,
  Eye,
  EyeClosed,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MerchantLogin } from "@/api-function/auth/login";

interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function ResetPasswordModal({
  isOpen,
  onClose,
  onSuccess,
}: ResetPasswordModalProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [generatedOtp, setGeneratedOtp] = useState<string>("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!phone) {
      setError("Please enter your phone number");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Generate OTP for testing
      const newOtp = MerchantLogin.generateOTP();
      setGeneratedOtp(newOtp);

      // Log the OTP for testing purposes
      console.log("🔑 Generated OTP for testing:", newOtp);

      // In a production environment, you would send this OTP via SMS
      // For testing, we'll show it in the UI

      setStep(2);
      setTimer(60);
      setCanResend(false);
      setSuccessMessage(`OTP sent successfully! (Test OTP: ${newOtp})`);

      // Auto-fill the OTP field for testing convenience
      setOtp(newOtp);

      setTimeout(() => {
        setSuccessMessage("OTP sent successfully! Please check your phone.");
      }, 3000);
    } catch (err) {
      console.error("Request OTP error:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otp || otp.length < 4) {
      setError("Please enter a valid OTP");
      return;
    }

    // For testing: verify the OTP matches the generated one
    if (otp !== generatedOtp) {
      setError("Invalid OTP. Please try again.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // OTP is valid, proceed to password reset
      setStep(3);
      setSuccessMessage("OTP verified! Please set your new password.");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate passwords
    if (password !== passwordConfirmation) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    // Validate password strength
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
    if (!passwordRegex.test(password)) {
      setError(
        "Password must contain at least one uppercase letter, one lowercase letter, and one number",
      );
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await MerchantLogin.resetPassword(
        phone,
        otp, // Use the OTP entered by the user (which should match generatedOtp)
        password,
        passwordConfirmation,
      );

      console.log("Reset password response:", response);

      // Check if the response has the expected structure
      if (response) {
        if (
          response.message === "Password reset successfully." ||
          response.token
        ) {
          setSuccessMessage("Password reset successfully! You can now login.");
          setTimeout(() => {
            onSuccess?.();
            onClose();
            // Reset form
            setPhone("");
            setOtp("");
            setGeneratedOtp("");
            setPassword("");
            setPasswordConfirmation("");
            setStep(1);
          }, 2000);
        } else if (response.message) {
          setError(response.message);
        } else {
          setError("Failed to reset password. Please try again.");
        }
      } else {
        setError("Invalid response from server. Please try again.");
      }
    } catch (err: any) {
      console.error("Reset password error:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setPhone("");
    setOtp("");
    setGeneratedOtp("");
    setPassword("");
    setPasswordConfirmation("");
    setError(null);
    setSuccessMessage(null);
    setStep(1);
    setTimer(0);
    onClose();
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Generate a new OTP
      const newOtp = MerchantLogin.generateOTP();
      setGeneratedOtp(newOtp);

      console.log("🔑 New OTP generated:", newOtp);

      setTimer(60);
      setCanResend(false);
      setOtp(newOtp); // Auto-fill for testing
      setSuccessMessage(`OTP resent! (Test OTP: ${newOtp})`);

      setTimeout(() => {
        setSuccessMessage("OTP resent successfully!");
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-white dark:bg-black rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {step === 1 && "Reset Password"}
            {step === 2 && "Enter OTP"}
            {step === 3 && "Set New Password"}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            {step === 1 && "Enter your phone number to receive an OTP"}
            {step === 2 && `We sent a 6-digit OTP to ${phone}`}
            {step === 3 && "Create a new password for your account"}
          </p>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
              <p className="text-sm text-green-600 dark:text-green-400">
                {successMessage}
              </p>
            </div>
          )}

          {/* Step 1: Phone Number */}
          {step === 1 && (
            <form onSubmit={handleRequestOTP} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-phone" className="font-medium">
                  Phone Number
                </Label>
                <div className="relative flex items-center">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="reset-phone"
                    type="tel"
                    placeholder="Enter your phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="h-11 pl-10 w-full bg-white dark:bg-black text-black dark:text-white border border-gray-300 dark:border-gray-700 rounded-lg"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || !phone}
                className="w-full custom-darkblue-button custom-shadow-effect play-fair disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Sending OTP...
                  </div>
                ) : (
                  "Send OTP"
                )}
              </button>
            </form>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp" className="font-medium">
                  Enter OTP
                </Label>
                <Input
                  id="otp"
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  className="h-11 w-full text-center text-2xl tracking-widest bg-white dark:bg-black text-black dark:text-white border border-gray-300 dark:border-gray-700 rounded-lg"
                  required
                  maxLength={6}
                  disabled={isLoading}
                />
                <div className="flex justify-between items-center mt-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {timer > 0 ? `Resend in ${timer}s` : "OTP expired"}
                  </span>
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={!canResend || isLoading}
                    className={`text-sm font-medium ${
                      canResend && !isLoading
                        ? "text-primary-color dark:text-blue-400 hover:underline"
                        : "text-gray-400 dark:text-gray-600 cursor-not-allowed"
                    }`}
                  >
                    Resend OTP
                  </button>
                </div>
                {/* Show generated OTP for testing */}
                {process.env.NODE_ENV === "development" && generatedOtp && (
                  <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <p className="text-xs text-yellow-600 dark:text-yellow-400">
                      Test OTP:{" "}
                      <span className="font-bold">{generatedOtp}</span>
                    </p>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading || otp.length < 4}
                className="w-full custom-darkblue-button custom-shadow-effect play-fair disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Verifying...
                  </div>
                ) : (
                  "Verify OTP"
                )}
              </button>
            </form>
          )}

          {/* Step 3: New Password */}
          {step === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password" className="font-medium">
                  New Password
                </Label>
                <div className="relative flex items-center">
                  <Lock className="absolute left-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password (min 6 characters)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-11 w-full bg-white dark:bg-black text-black dark:text-white border border-gray-300 dark:border-gray-700 rounded-lg"
                    required
                    minLength={6}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="absolute right-3 text-gray-400 hover:text-black dark:hover:text-white"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {password ? (
                      showPassword ? (
                        <Eye className="h-5 w-5" />
                      ) : (
                        <EyeClosed className="h-5 w-5" />
                      )
                    ) : null}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="font-medium">
                  Confirm Password
                </Label>
                <div className="relative flex items-center">
                  <Lock className="absolute left-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your new password"
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    className="pl-10 pr-10 h-11 w-full bg-white dark:bg-black text-black dark:text-white border border-gray-300 dark:border-gray-700 rounded-lg"
                    required
                    minLength={6}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="absolute right-3 text-gray-400 hover:text-black dark:hover:text-white"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {passwordConfirmation ? (
                      showConfirmPassword ? (
                        <Eye className="h-5 w-5" />
                      ) : (
                        <EyeClosed className="h-5 w-5" />
                      )
                    ) : null}
                  </button>
                </div>
              </div>

              <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                <p>Password must contain:</p>
                <ul className="list-disc list-inside ml-2">
                  <li>At least 6 characters</li>
                  <li>At least one uppercase letter</li>
                  <li>At least one lowercase letter</li>
                  <li>At least one number</li>
                </ul>
              </div>

              <button
                type="submit"
                disabled={isLoading || !password || !passwordConfirmation}
                className="w-full custom-darkblue-button custom-shadow-effect play-fair disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Resetting Password...
                  </div>
                ) : (
                  "Reset Password"
                )}
              </button>
            </form>
          )}

          <div className="mt-4 text-center">
            <button
              onClick={handleClose}
              // className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              className="border rounded-md px-3 py-1 custom-shadow-effect play-fair w-full"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
